import {
  type Color,
  DataTexture,
  type Material,
  DoubleSide,
  FrontSide,
  MeshToonMaterial,
  NearestFilter,
  RedFormat,
  type Texture,
  UnsignedByteType,
  Vector2,
  type WebGLProgramParametersWithUniforms,
} from 'three';

/** Outline classes written to the alpha of the normal buffer and read by the post pass (05 §8). */
export const OUTLINE = {
  none: 0,
  /** Level blocks and props: inner silhouette darkening and crease highlights. */
  block: 1,
  /** Monsters, items: a 1 px outer ink outline. */
  character: 2,
  /** Bubbles: outer outline in the bubble's own rim colour, not ink. */
  bubble: 3,
  /** Bub and Bob: outer outline; the post pass may swap it for a pale rim in dark levels. */
  player: 5,
} as const;
export type OutlineClass = (typeof OUTLINE)[keyof typeof OUTLINE];

/** Uniforms every screen-anchored pattern needs; one shared instance, updated by the pipeline. */
export const sharedUniforms = {
  uCamTexel: { value: new Vector2() },
  uTime: { value: 0 },
};

/**
 * Toon ramp indexed by half-Lambert (N·L * 0.5 + 0.5). Faces pointing away from the light
 * get nothing from it (ambient only); lit faces fall into bands.
 */
export function createGradientMap(
  steps: readonly number[] = [0, 0, 0, 0, 0.34, 0.6, 0.85, 1],
): DataTexture {
  const data = new Uint8Array(steps.map((s) => Math.round(s * 255)));
  const tex = new DataTexture(data, steps.length, 1, RedFormat, UnsignedByteType);
  tex.minFilter = NearestFilter;
  tex.magFilter = NearestFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

export type Shader = WebGLProgramParametersWithUniforms;

const MRT_DECL = /* glsl */ `
layout(location = 1) out highp vec4 gNormalId;
uniform float uOutlineClass;
`;

const MRT_WRITE = /* glsl */ `
  gNormalId = vec4(normalize(normal) * 0.5 + 0.5, uOutlineClass);
`;

/** Adds the second render-target output (view normal + outline class) to a built-in material. */
export function patchMrt(shader: Shader, outlineClass: OutlineClass): void {
  shader.uniforms['uOutlineClass'] = { value: outlineClass / 8 };
  shader.fragmentShader = shader.fragmentShader
    .replace('void main() {', `${MRT_DECL}\nvoid main() {`)
    .replace('#include <dithering_fragment>', `#include <dithering_fragment>\n${MRT_WRITE}`);
}

export interface ToonOptions {
  gradientMap: Texture;
  outline: OutlineClass;
  doubleSided?: boolean;
  /** Emissive colour (fire, lightning, a lit fruit): bypasses lighting. */
  emissive?: Color;
  emissiveIntensity?: number;
  /**
   * Tiles the surface is pulled toward the camera in the depth test only (05 §6.3). Actors
   * use it: in the original every sprite drew over the tiles, and Bub jumps up THROUGH
   * platforms, so an actor must never be hidden by a block it overlaps. Its screen position
   * is untouched, and the shadow pass does not see the bias, so its shadow falls true.
   */
  depthBias?: number;
  /**
   * One flat colour instead of the vertex colours (linear): the C64 draws a dead monster's sprite
   * all light blue while it flies off to become fruit.
   */
  tint?: Color;
}

/** Vertex-shader lines that pull a surface `uDepthBias` tiles toward the camera in the depth test only. */
export const DEPTH_BIAS = /* glsl */ `
  // Orthographic: moving b units toward the camera changes clip z by projectionMatrix[2][2] * b.
  gl_Position.z += projectionMatrix[2][2] * uDepthBias;
`;

/** Standard vertex-coloured toon material for every block, prop, creature and item. */
export function createToonMaterial(opts: ToonOptions): MeshToonMaterial {
  const material = new MeshToonMaterial({
    vertexColors: opts.tint === undefined,
    gradientMap: opts.gradientMap,
    side: opts.doubleSided ? DoubleSide : FrontSide,
  });
  if (opts.tint) material.color.copy(opts.tint);
  if (opts.emissive) {
    material.emissive.copy(opts.emissive);
    material.emissiveIntensity = opts.emissiveIntensity ?? 1;
  }
  const bias = opts.depthBias ?? 0;
  material.onBeforeCompile = (shader) => {
    patchMrt(shader, opts.outline);
    if (bias === 0) return;
    shader.uniforms['uDepthBias'] = { value: bias };
    shader.vertexShader = shader.vertexShader
      .replace('void main() {', 'uniform float uDepthBias;\nvoid main() {')
      .replace('#include <project_vertex>', `#include <project_vertex>\n${DEPTH_BIAS}`);
  };
  // One program per variant: the outline class is a per-material uniform, not a define.
  material.customProgramCacheKey = () => (bias === 0 ? 'toon-mrt' : 'toon-mrt-bias');
  return material;
}

/**
 * Self-lit surfaces (fire, lightning, the lit window of a bonus): the vertex colour is
 * added as emission on top of normal lighting, so they hold their colour in dark levels.
 * `depthBias` as for `createToonMaterial`: a bolt flying through a wall stays in front of it.
 */
export function createEmissiveMaterial(gradientMap: Texture, boost = 1.6, depthBias = 0): MeshToonMaterial {
  const material = new MeshToonMaterial({ vertexColors: true, gradientMap });
  // Kept on the material so the boost can change live (reduce flashing softens every glow).
  const boostUniform = { value: boost };
  material.userData['boost'] = boostUniform;
  material.onBeforeCompile = (shader) => {
    patchMrt(shader, OUTLINE.none);
    shader.uniforms['uBoost'] = boostUniform;
    shader.fragmentShader = shader.fragmentShader
      .replace('void main() {', 'uniform float uBoost;\nvoid main() {')
      .replace(
        '#include <emissivemap_fragment>',
        '#include <emissivemap_fragment>\n  totalEmissiveRadiance += vColor.rgb * uBoost;',
      );
    if (depthBias === 0) return;
    shader.uniforms['uDepthBias'] = { value: depthBias };
    shader.vertexShader = shader.vertexShader
      .replace('void main() {', 'uniform float uDepthBias;\nvoid main() {')
      .replace('#include <project_vertex>', `#include <project_vertex>\n${DEPTH_BIAS}`);
  };
  material.customProgramCacheKey = () => (depthBias === 0 ? 'toon-mrt-emissive' : 'toon-mrt-emissive-bias');
  return material;
}

/** Change a self-lit material's glow (made by `createEmissiveMaterial`) without recompiling it. */
export function setEmissiveBoost(material: Material, boost: number): void {
  const u: unknown = material.userData['boost'];
  if (typeof u === 'object' && u !== null && 'value' in u) (u as { value: number }).value = boost;
}

/** MRT patch for unlit materials (points, lines): no outline, camera-facing normal. */
export function patchMrtUnlit(shader: Shader): void {
  shader.fragmentShader = shader.fragmentShader
    .replace('void main() {', 'layout(location = 1) out highp vec4 gNormalId;\nvoid main() {')
    .replace(
      '#include <premultiplied_alpha_fragment>',
      '#include <premultiplied_alpha_fragment>\n  gNormalId = vec4(0.5, 0.5, 1.0, 0.0);',
    );
}
